import { useState } from "react";
import {
  RiAddLine,
  RiArrowLeftLine,
  RiMailLine,
  RiWhatsappLine,
  RiTimeLine,
  RiSettings3Line,
  RiBarChartLine,
  // RiTranslate2Line,
  RiFileTextLine,
  RiSaveLine,
} from "react-icons/ri";
import { useNavigate } from "react-router-dom";

const PaymentReminders = () => {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState([
    {
      id: 1,
      customerName: "Tech Solutions Inc",
      invoiceNumber: "INV-2024-001",
      amount: 2499.99,
      dueDate: "2024-03-15",
      remindersSent: 2,
      lastReminder: "2024-02-25",
      channel: "Email",
      status: "Scheduled",
    },
    {
      id: 2,
      customerName: "Design Studio Pro",
      invoiceNumber: "INV-2024-002",
      amount: 1899.99,
      dueDate: "2024-03-10",
      remindersSent: 1,
      lastReminder: "2024-02-20",
      channel: "WhatsApp",
      status: "Sent",
    },
  ]);

  const [automationRules] = useState([
    {
      id: 1,
      name: "3 Days Before Due",
      description: "Send reminder 3 days before due date",
      status: "Active",
      channel: "Email",
    },
    {
      id: 2,
      name: "On Due Date",
      description: "Send reminder on the due date",
      status: "Active",
      channel: "Both",
    },
    {
      id: 3,
      name: "3 Days After Due",
      description: "Send follow-up 3 days after due date",
      status: "Active",
      channel: "WhatsApp",
    },
  ]);

  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("en");

  const [templates, setTemplates] = useState([
    {
      id: 1,
      name: "First Reminder",
      subject: "Invoice Payment Reminder",
      content:
        "Dear {{customerName}},\n\nThis is a friendly reminder that invoice {{invoiceNumber}} for {{amount}} is due on {{dueDate}}.",
      language: "en",
      useCount: 145,
      responseRate: 68,
    },
    {
      id: 2,
      name: "Final Notice",
      subject: "Final Payment Notice",
      content:
        "Dear {{customerName}},\n\nYour invoice {{invoiceNumber}} for {{amount}} is overdue. Please arrange payment immediately.",
      language: "en",
      useCount: 89,
      responseRate: 75,
    },
  ]);

  const [analytics] = useState({
    responseRates: {
      email: 65,
      whatsapp: 82,
      overall: 71,
    },
    bestTimes: [
      { time: "10:00 AM", rate: 78 },
      { time: "2:00 PM", rate: 72 },
      { time: "4:00 PM", rate: 68 },
    ],
    templatePerformance: [
      { name: "First Reminder", success: 68, total: 100 },
      { name: "Final Notice", success: 75, total: 100 },
    ],
  });

  const ReminderTemplates = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Reminder Templates</h2>
          <button
            className="btn btn-secondary"
            onClick={() => setShowTemplateModal(false)}
          >
            Close
          </button>
        </div>

        <div className="space-y-6">
          {/* Template List */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {templates.map((template) => (
              <div key={template.id} className="card">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold">{template.name}</h3>
                    <p className="text-sm text-gray-500">
                      Language: {template.language.toUpperCase()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button className="btn btn-icon">
                      <RiFileTextLine className="w-5 h-5" />
                    </button>
                    <button className="btn btn-icon text-red-600">
                      <RiSettings3Line className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <div>
                    <label className="label">Subject</label>
                    <p className="text-sm">{template.subject}</p>
                  </div>
                  <div>
                    <label className="label">Content</label>
                    <p className="text-sm whitespace-pre-line">
                      {template.content}
                    </p>
                  </div>
                </div>
                <div className="flex justify-between mt-4 pt-4 border-t">
                  <span className="text-sm text-gray-500">
                    Used {template.useCount} times
                  </span>
                  <span className="text-sm text-green-600">
                    {template.responseRate}% response rate
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* New Template Button */}
          <button className="btn btn-primary w-full flex items-center justify-center gap-2">
            <RiAddLine className="w-5 h-5" />
            Create New Template
          </button>
        </div>
      </div>
    </div>
  );

  const ReminderAnalytics = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Reminder Analytics</h2>
          <button
            className="btn btn-secondary"
            onClick={() => setShowAnalyticsModal(false)}
          >
            Close
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Response Rates */}
          <div className="card">
            <h3 className="font-semibold mb-4">Response Rates</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span>Email</span>
                  <span>{analytics.responseRates.email}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full">
                  <div
                    className="h-2 bg-blue-500 rounded-full"
                    style={{ width: `${analytics.responseRates.email}%` }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span>WhatsApp</span>
                  <span>{analytics.responseRates.whatsapp}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full">
                  <div
                    className="h-2 bg-green-500 rounded-full"
                    style={{ width: `${analytics.responseRates.whatsapp}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Best Times */}
          <div className="card">
            <h3 className="font-semibold mb-4">Best Times to Send</h3>
            <div className="space-y-4">
              {analytics.bestTimes.map((time, index) => (
                <div key={index}>
                  <div className="flex justify-between mb-1">
                    <span>{time.time}</span>
                    <span>{time.rate}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full">
                    <div
                      className="h-2 bg-purple-500 rounded-full"
                      style={{ width: `${time.rate}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Template Performance */}
          <div className="card">
            <h3 className="font-semibold mb-4">Template Performance</h3>
            <div className="space-y-4">
              {analytics.templatePerformance.map((template, index) => (
                <div key={index}>
                  <div className="flex justify-between mb-1">
                    <span>{template.name}</span>
                    <span>{template.success}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full">
                    <div
                      className="h-2 bg-orange-500 rounded-full"
                      style={{ width: `${template.success}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-full mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/billing")}
            className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors"
          >
            <RiArrowLeftLine className="w-5 h-5" />
          </button>
          <h1 className="text-3xl font-bold text-gray-900">
            Payment Reminders
          </h1>
        </div>
        <div className="flex gap-3">
          <button
            className="btn btn-secondary flex items-center gap-2"
            onClick={() => setShowTemplateModal(true)}
          >
            <RiFileTextLine className="w-5 h-5" />
            Manage Templates
          </button>
          <button
            className="btn btn-secondary flex items-center gap-2"
            onClick={() => setShowAnalyticsModal(true)}
          >
            <RiBarChartLine className="w-5 h-5" />
            View Analytics
          </button>
          <button className="btn btn-primary flex items-center gap-2">
            <RiAddLine className="w-5 h-5" />
            Create Reminder
          </button>
        </div>
      </div>

      {/* Automation Rules */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4">Automation Rules</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {automationRules.map((rule) => (
            <div
              key={rule.id}
              className="border rounded-lg p-4 hover:border-primary-200"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold">{rule.name}</h3>
                  <p className="text-sm text-gray-500">{rule.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  {rule.channel.includes("Email") && (
                    <RiMailLine className="w-5 h-5 text-gray-400" />
                  )}
                  {rule.channel.includes("WhatsApp") && (
                    <RiWhatsappLine className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    rule.status === "Active"
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {rule.status}
                </span>
                <button className="text-gray-400 hover:text-gray-600">
                  <RiSettings3Line className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Reminders Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-4 px-4">Customer</th>
                <th className="text-left py-4 px-4">Invoice</th>
                <th className="text-left py-4 px-4">Amount</th>
                <th className="text-left py-4 px-4">Due Date</th>
                <th className="text-left py-4 px-4">Reminders Sent</th>
                <th className="text-left py-4 px-4">Last Reminder</th>
                <th className="text-left py-4 px-4">Status</th>
                <th className="text-right py-4 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reminders.map((reminder) => (
                <tr key={reminder.id} className="border-b">
                  <td className="py-4 px-4">{reminder.customerName}</td>
                  <td className="py-4 px-4">{reminder.invoiceNumber}</td>
                  <td className="py-4 px-4">${reminder.amount}</td>
                  <td className="py-4 px-4">
                    {new Date(reminder.dueDate).toLocaleDateString()}
                  </td>
                  <td className="py-4 px-4">{reminder.remindersSent}</td>
                  <td className="py-4 px-4">
                    {new Date(reminder.lastReminder).toLocaleDateString()}
                  </td>
                  <td className="py-4 px-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        reminder.status === "Sent"
                          ? "bg-green-100 text-green-800"
                          : "bg-blue-100 text-blue-800"
                      }`}
                    >
                      {reminder.status}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex justify-end gap-2">
                      <button className="p-2 hover:bg-gray-100 rounded-lg text-gray-600">
                        <RiMailLine className="w-5 h-5" />
                      </button>
                      <button className="p-2 hover:bg-gray-100 rounded-lg text-gray-600">
                        <RiWhatsappLine className="w-5 h-5" />
                      </button>
                      <button className="p-2 hover:bg-gray-100 rounded-lg text-gray-600">
                        <RiTimeLine className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showTemplateModal && <ReminderTemplates />}
      {showAnalyticsModal && <ReminderAnalytics />}
    </div>
  );
};

export default PaymentReminders;
